/**
 * What has to be re-read from the server after an admin acts on a CPM
 * submission.
 *
 * The campaign-submissions page reads two things: the paginated ROWS, and the
 * campaign-global STATS (earnings, views, eligible count, daily series). Stats
 * are by far the expensive half — the backend aggregates every eligible
 * submission of the campaign to build them — so refetching them after an
 * action that cannot have moved them is pure latency, and it lands right where
 * the admin is about to click again.
 *
 * Rows usually need nothing either: the mutations return the updated
 * `AdminCpmCampaignSubmission`, which Apollo's normalized cache writes straight
 * into the list. Only actions that can change submissions OTHER than the one
 * acted on need the rows re-read.
 *
 * Getting `stats` wrong leaves a stale earnings total on screen, so every
 * action is listed explicitly and anything unrecognized refreshes both.
 */
export type AdminSubmissionAction =
  | 'approval'
  | 'observation'
  | 'hashtagValidation'
  | 'cpmOverride'
  | 'resolveSuspectedDuplicate'
  | 'disqualifyUser'
  | 'undisqualifyUser'
  | 'disqualifyAccount'
  | 'liftAccountDisqualification'
  | 'linkReviewGroup'
  | 'forceUpdateViews'
  | 'appealAccept'
  | 'appealKeep'
  | 'appealRequestMore'
  | 'bulkApprove'
  | 'bulkDisapprove';

export interface SubmissionRefreshPlan {
  rows: boolean;
  stats: boolean;
}

export const ADMIN_SUBMISSION_ACTIONS: AdminSubmissionAction[] = [
  'approval',
  'observation',
  'hashtagValidation',
  'cpmOverride',
  'resolveSuspectedDuplicate',
  'disqualifyUser',
  'undisqualifyUser',
  'disqualifyAccount',
  'liftAccountDisqualification',
  'linkReviewGroup',
  'forceUpdateViews',
  'appealAccept',
  'appealKeep',
  'appealRequestMore',
  'bulkApprove',
  'bulkDisapprove',
];

const PLANS: Record<AdminSubmissionAction, SubmissionRefreshPlan> = {
  // Money-bearing, one row: the mutation reports the row's new payout, the
  // campaign aggregate has to be re-read.
  approval: { rows: false, stats: true },
  cpmOverride: { rows: false, stats: true },
  resolveSuspectedDuplicate: { rows: false, stats: true },

  // One row, no money: the mutation result is the whole update.
  observation: { rows: false, stats: false },
  hashtagValidation: { rows: false, stats: false },
  appealKeep: { rows: false, stats: false },
  appealRequestMore: { rows: false, stats: false },

  // Only queues a scrape — the new views arrive later, not in this response.
  forceUpdateViews: { rows: false, stats: false },

  // Can rewrite submissions the mutation says nothing about.
  disqualifyUser: { rows: true, stats: true },
  undisqualifyUser: { rows: true, stats: true },
  // An account ban/lift rewrites every clip of that account and moves money.
  disqualifyAccount: { rows: true, stats: true },
  liftAccountDisqualification: { rows: true, stats: true },
  linkReviewGroup: { rows: true, stats: true },
  appealAccept: { rows: true, stats: true },
  bulkApprove: { rows: true, stats: true },
  // Answers counts only; rejected rows leave the pending filter.
  bulkDisapprove: { rows: true, stats: true },
};

export function resolveSubmissionRefreshPlan(
  action: AdminSubmissionAction,
): SubmissionRefreshPlan {
  return PLANS[action] ?? { rows: true, stats: true };
}
